"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Group,
  Button,
  Accordion,
  Paper,
  Anchor,
} from "@mantine/core";
import Link from "next/link";
import { trackCtaClick } from "@/lib/analytics";
import { colorSeasons } from "@/constants";
import classes from "./seasonal.module.css";

// Section counter helper — matches how-to-do-color-analysis/content.tsx SectionHeader
function SectionHeader({
  n,
  label,
  title,
}: {
  n: number;
  label: string;
  title: string;
}) {
  return (
    <Box mb={24}>
      <p className={classes.kicker}>
        N&ordm; {String(n).padStart(2, "0")} &mdash; {label}
      </p>
      <h2 className={classes.sectionTitle}>{title}</h2>
      <hr className={classes.hairline} />
    </Box>
  );
}

const faqs = [
  {
    question: "What is seasonal color analysis?",
    answer:
      "Seasonal color analysis is a method for matching your skin's undertone, contrast, and depth to one of 12 color palettes, called seasons. Once you know your season, you know which clothing and makeup colors make your skin look clear and healthy — and which ones work against you. The system groups the 12 seasons into 4 families: Spring, Summer, Autumn, and Winter.",
  },
  {
    question: "How do I find my color season?",
    answer:
      "There are three free ways to find your season: a short quiz that asks about your skin, hair, and eye coloring; an AI photo analysis that reads your undertone and contrast directly from two photos; or virtual draping, where digital color swatches are placed against your photo one at a time to see which ones flatter you most.",
  },
  {
    question: "What are the 12 color seasons?",
    answer:
      "The 12 seasons fall into 4 families of 3 subseasons each. Spring: Light, True, and Bright Spring. Summer: Light, True, and Cool Summer. Autumn: Soft, True, and Deep Autumn. Winter: Cool, True, and Deep Winter. Each subseason has its own palette of best colors based on undertone, depth, and how saturated or muted the coloring is.",
  },
  {
    question: "How accurate is seasonal color analysis?",
    answer:
      "It depends on the method. A quiz is a good first pass and usually gets people to the right season family. AI photo analysis is more precise because it reads your actual coloring instead of relying on self-reported answers. Professional in-person draping is the gold standard. Most people land in the correct family from a quiz alone, and only need a more precise method to narrow down the exact subseason.",
  },
  {
    question: "What does seasonal color analysis cost?",
    answer:
      "Our quiz is free. AI photo analysis is $24.99 and gives you your full 12-season result with a personalized palette. Professional in-person sessions with a color analyst typically run $150–400.",
  },
  {
    question: "Is seasonal color analysis the same as personal color analysis?",
    answer:
      "Yes. They're the same practice under two names. \"Personal color analysis\" is the broader term for matching colors to your natural coloring, and \"seasonal\" refers specifically to the 12-season naming system most analysts use to organize the results.",
  },
  {
    question: "Can my color season change?",
    answer:
      "No. Your season is based on your skin's undertone and eye pattern, both of which are fixed by genetics and don't change over time. What you do to your hair, your tan, or your makeup can change what looks good on you in the moment, but it doesn't change your underlying season.",
  },
];

const SEASON_ORDER: { key: string; label: string; temperament: string }[] = [
  {
    key: "SPRING",
    label: "Spring",
    temperament: "Warm and light-to-bright — golden undertones, high clarity.",
  },
  {
    key: "SUMMER",
    label: "Summer",
    temperament: "Cool and soft — muted undertones, gentle contrast.",
  },
  {
    key: "AUTUMN",
    label: "Autumn",
    temperament: "Warm and muted — earthy undertones, rich depth.",
  },
  {
    key: "WINTER",
    label: "Winter",
    temperament: "Cool and bright — high contrast, saturated clarity.",
  },
];

const SUBSEASON_BLURBS: Record<string, string> = {
  "SPRING.LIGHT": "Warm, delicate, sun-washed pastels",
  "SPRING.TRUE": "Golden, vivid, garden-fresh color",
  "SPRING.BRIGHT": "Warm-leaning, saturated, high-energy",
  "SUMMER.LIGHT": "Cool, airy, powdery softness",
  "SUMMER.TRUE": "Cool, dusty, rose-gray calm",
  "SUMMER.COOL": "Blue-based, icy, softly muted",
  "AUTUMN.SOFT": "Muted, mossy, gently warm",
  "AUTUMN.TRUE": "Earthy, spiced, golden richness",
  "AUTUMN.DEEP": "Dark, warm, espresso-and-ember",
  "WINTER.COOL": "Icy, blue-based, crisp contrast",
  "WINTER.TRUE": "Pure hues, black-and-jewel drama",
  "WINTER.DEEP": "Deep, cool, and intense",
};

const SUBSEASON_KEYS: Record<string, string[]> = {
  SPRING: ["LIGHT", "TRUE", "BRIGHT"],
  SUMMER: ["LIGHT", "TRUE", "COOL"],
  AUTUMN: ["SOFT", "TRUE", "DEEP"],
  WINTER: ["COOL", "TRUE", "DEEP"],
};

function titleCase(word: string) {
  return word.charAt(0) + word.slice(1).toLowerCase();
}

export default function SeasonalColorAnalysisContent({ locale }: { locale: string }) {
  void locale;

  let sectionNumber = 0;
  const nextSection = () => {
    sectionNumber += 1;
    return sectionNumber;
  };

  const linkStyle: React.CSSProperties = {
    color: "inherit",
    textDecoration: "underline",
  };

  return (
    <Container size="xl" py={48}>
      <Stack gap={0}>
        {/* ---------- Hero ---------- */}
        <Box className={classes.hero} mb={56}>
          <p className={classes.heroKicker}>THE COMPLETE GUIDE</p>
          <h1 className={classes.heroTitle}>Seasonal Color Analysis</h1>
          <p className={classes.heroTagline}>
            Match your skin&apos;s undertone, contrast, and depth to one of 12 color palettes
            &mdash; and know, permanently, which colors work for you.
          </p>

          <div className={classes.heroStats}>
            <div className={classes.heroStat}>
              <p className={classes.heroStatLabel}>Color seasons</p>
              <p className={classes.heroStatValue}>12</p>
            </div>
            <div className={classes.heroStat}>
              <p className={classes.heroStatLabel}>Signals read</p>
              <p className={classes.heroStatValue}>3</p>
            </div>
            <div className={classes.heroStat}>
              <p className={classes.heroStatLabel}>Digital analysis</p>
              <p className={classes.heroStatValue}>~1 min</p>
            </div>
          </div>

          <Group gap="sm" wrap="wrap" align="center" mt={28}>
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("sca_early")}
              size="md"
              radius={999}
              fw={700}
            >
              Get your color analysis
            </Button>
            <Anchor component={Link} href="/color-analysis-quiz" fz="sm" c="#6f6a64">
              or take the free 5-question quiz
            </Anchor>
          </Group>
        </Box>

        {/* ---------- Lede ---------- */}
        <Box mb={56}>
          <Text className={classes.lede}>
            Seasonal color analysis combines three properties of your natural coloring &mdash;
            undertone, contrast, and depth &mdash; to place you in one of 4 seasons and, more
            precisely, one of 12 subseasons. Each subseason carries its own calibrated palette:
            colors inside it tend to make your skin look clearer and your face more put-together;
            colors outside it can wash you out, even when they&apos;re colors you like.
          </Text>
        </Box>

        {/* ---------- N01 The System / Palette plates ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="The System" title="Four seasons, twelve ways to wear color" />
          <Text mb={8} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Every subseason has its own palette, built from the same three measurements. Select
            one to see the full breakdown.
          </Text>

          {SEASON_ORDER.map(({ key: seasonKey, label: seasonLabel, temperament }) => (
            <Box key={seasonKey} className={classes.seasonBlock}>
              <h3 className={classes.seasonName}>{seasonLabel}</h3>
              <p className={classes.seasonTemperament}>{temperament}</p>
              {SUBSEASON_KEYS[seasonKey].map((subKey) => {
                const subseason = colorSeasons[seasonKey].subseasons[subKey];
                return (
                  <Link
                    key={`${seasonKey}-${subKey}`}
                    href={`/color-palette/${seasonKey.toLowerCase()}/${subKey.toLowerCase()}`}
                    className={classes.subseasonRow}
                  >
                    <div className={classes.subseasonMeta}>
                      <p className={classes.subseasonName}>
                        {titleCase(subKey)} {seasonLabel}
                      </p>
                      <p className={classes.subseasonDesc}>{SUBSEASON_BLURBS[`${seasonKey}.${subKey}`]}</p>
                    </div>
                    <div className={classes.swatchStrip}>
                      {subseason.primaryColors.slice(0, 7).map((color) => (
                        <div
                          key={color.key}
                          className={classes.swatch}
                          style={{ background: color.hex }}
                        />
                      ))}
                    </div>
                  </Link>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* ---------- N02 The Method ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="The Method" title="What an analysis actually reads" />
          <ul className={classes.numberedList}>
            {[
              {
                title: "Undertone.",
                text: "Whether your skin leans warm (golden, peachy) or cool (pink, blue-ish) beneath its surface color. This is independent of how light or dark your skin is.",
              },
              {
                title: "Contrast.",
                text: "The gap between your darkest and lightest features — hair, eyes, and skin. High contrast handles bold color combinations; low contrast looks best when colors stay close in depth.",
              },
              {
                title: "Depth.",
                text: "How light or deep your overall coloring reads. Depth, together with undertone and contrast, narrows you down from a season family to your exact subseason.",
              },
            ].map((item, i) => (
              <li key={i} className={classes.numberedItem}>
                <span className={classes.numberFigure}>{String(i + 1).padStart(2, "0")}</span>
                <span className={classes.numberedItemText}>
                  <Text fw={700} span>{item.title}</Text> {item.text}
                </span>
              </li>
            ))}
          </ul>
        </Box>

        {/* ---------- Mid-page CTA (slim) ---------- */}
        <Box mb={56} className={`${classes.ctaBand} ${classes.ctaBandSlim}`}>
          <Text className={classes.ctaHeadline} fz={{ base: 18, md: 20 }}>
            Our AI reads all three properties from two photos in about 90 seconds.
          </Text>
          <Group gap="sm" wrap="wrap" mt="md">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("sca_mid")}
              size="md"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Analyze my photos
            </Button>
          </Group>
        </Box>

        {/* ---------- N03 Your Options ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Your Options" title="Three ways to find your season" />
          <ul className={classes.editorialList}>
            {[
              {
                href: "/color-analysis-quiz",
                name: "Free quiz",
                desc: "Answer 5 questions about your skin, hair, and eyes to get your season family.",
                meta: "5 questions, 2 minutes",
              },
              {
                href: "/ai-color-analysis",
                name: "AI photo analysis",
                desc: "Upload two photos and get your exact 12-season result with a full palette.",
                meta: "Most accurate · $24.99",
              },
              {
                href: "/virtual-color-draping",
                name: "Virtual draping",
                desc: "See digital color swatches held up against your own photo, one at a time.",
                meta: "Free",
              },
            ].map((option) => (
              <li key={option.href} className={classes.editorialListItem}>
                <Group justify="space-between" align="baseline" wrap="wrap" gap={8}>
                  <Box>
                    <Text className={classes.serif} fz="1.05rem" fw={500} c="#1a1a1a" mb={4}>
                      {option.name}
                    </Text>
                    <Text fz="sm" c="#3a3632">
                      {option.desc}
                    </Text>
                  </Box>
                  <Anchor component={Link} href={option.href} fz="sm" fw={600} c="#1a1a1a">
                    {option.meta} &rarr;
                  </Anchor>
                </Group>
              </li>
            ))}
          </ul>
          <Text mt={20} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Want to try it yourself first?{" "}
            <Link href="/how-to-do-color-analysis" style={linkStyle}>
              Here&apos;s how to do color analysis at home
            </Link>
            , or browse every palette laid out in the{" "}
            <Link href="/color-seasons-chart" style={linkStyle}>
              12 season color chart
            </Link>
            .
          </Text>
        </Box>

        {/* ---------- N04 FAQ ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Questions" title="Questions people actually ask" />
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500} className={classes.serif} fz="lg">
                    {faq.question}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="#6f6a64">
                    {faq.answer}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* ---------- Bottom CTA ---------- */}
        <Paper className={classes.ctaBand} ta="center" mb={40}>
          <Text
            tt="uppercase"
            fz={11}
            fw={600}
            c="rgba(255,255,255,0.6)"
            style={{ letterSpacing: "0.14em" }}
            mb={8}
          >
            FIND YOUR SEASON
          </Text>
          <Title order={2} className={classes.ctaHeadline} fz={{ base: 26, md: 34 }}>
            Find your season now
          </Title>
          <Text className={classes.ctaSubtitle} fz="md" maw={560} mx="auto">
            Get your exact 12-season result, your personalized palette, and the colors to avoid
            &mdash; from two photos, in about 90 seconds.
          </Text>
          <Group justify="center" gap="md" wrap="wrap">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("sca_bottom")}
              size="lg"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Get your color analysis
            </Button>
          </Group>
          <Text fz="sm" mt={16} style={{ color: "rgba(255,255,255,0.7)" }}>
            Prefer free?{" "}
            <Link href="/color-analysis-quiz" style={{ color: "inherit", textDecoration: "underline" }}>
              Take the quiz instead
            </Link>
            .
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}
